import React, { useState, useEffect } from 'react';
import type { ReceiptPrinter } from '../../types';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';

interface PrinterModalProps {
  printer: ReceiptPrinter | null;
  onClose: () => void;
  onSave: (printer: ReceiptPrinter) => void;
}

const PrinterModal: React.FC<PrinterModalProps> = ({ printer, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    enabled: true,
  });
  const [id, setId] = useState<string | null>(null);

  useEffect(() => {
    if (printer) {
      setFormData({
        name: printer.name,
        enabled: printer.enabled,
      });
      setId(printer.id);
    } else {
      setFormData({ name: '', enabled: true });
      setId(null);
    }
  }, [printer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const nameKeyboard = useSoftKeyboardField({
    id: 'printer-modal-name',
    type: 'alphanumeric',
    label: 'Printer Name',
    value: formData.name,
    onChange: (v) => setFormData((prev) => ({ ...prev, name: v })),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Please provide a name for the printer.');
      return;
    }

    onSave({
      id: id || '',
      name: formData.name,
      enabled: formData.enabled,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto my-auto">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">{printer ? 'Edit Printer' : 'Add New Printer'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Printer Name</label>
            <input type="text" name="name" value={formData.name} placeholder="e.g., Kitchen Printer or Bar Printer" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-pink-500 focus:border-pink-500" data-soft-keyboard-handled {...nameKeyboard} required />
          </div>
          <p className="text-xs text-gray-500 mb-4">Receipts print via the browser's print dialog (Browser Print).</p>
          <div className="mb-6">
            <label className="flex items-center">
              <input type="checkbox" name="enabled" checked={formData.enabled} onChange={handleChange} className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500" />
              <span className="ml-2 text-sm text-gray-900">Enabled</span>
            </label>
          </div>
          <div className="flex justify-end space-x-4">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600">Save Printer</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PrinterModal;
